/**
 * Riusable Recharts wrappers for the DVR ProSuite dashboards.
 * All charts are responsive and inherit from the existing palette (blue/emerald/amber/rose).
 */
import {
  BarChart, Bar, PieChart, Pie, LineChart, Line, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid,
} from "recharts";

const COLORS_LEVEL = {
  basso: "#10b981",
  moderato: "#f59e0b",
  significativo: "#f97316",
  alto: "#ef4444",
};

const COLORS_STATUS = {
  aperta: "#94a3b8",
  in_corso: "#3b82f6",
  completata: "#10b981",
  annullata: "#cbd5e1",
  scaduta: "#ef4444",
};

const COLORS_CATEGORY = [
  "#1e3a8a", "#0ea5e9", "#06b6d4", "#10b981", "#84cc16",
  "#eab308", "#f59e0b", "#f97316", "#ef4444", "#a855f7",
  "#ec4899", "#64748b",
];

function EmptyChart({ label = "Nessun dato", testId }) {
  return (
    <div className="flex h-[220px] items-center justify-center rounded-md border border-dashed border-slate-200 text-xs text-slate-400" data-testid={testId}>
      {label}
    </div>
  );
}

export function RisksByCategoryChart({ data, testId = "chart-risks-by-category" }) {
  if (!data || data.length === 0) return <EmptyChart testId={testId} />;
  return (
    <div className="h-[260px] w-full" data-testid={testId}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
          <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={130} />
          <Tooltip />
          <Bar dataKey="value" radius={[0, 4, 4, 0]}>
            {data.map((_, idx) => (
              <Cell key={idx} fill={COLORS_CATEGORY[idx % COLORS_CATEGORY.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function RisksByLevelChart({ data, testId = "chart-risks-by-level" }) {
  if (!data || data.every((d) => !d.value)) return <EmptyChart testId={testId} />;
  return (
    <div className="h-[260px] w-full" data-testid={testId}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={86} innerRadius={48} paddingAngle={2}>
            {data.map((entry, idx) => (
              <Cell key={idx} fill={COLORS_LEVEL[entry.name] || "#94a3b8"} />
            ))}
          </Pie>
          <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
          <Tooltip />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

export function DeadlinesByMonthChart({ data, testId = "chart-deadlines-by-month" }) {
  if (!data || data.length === 0) return <EmptyChart testId={testId} />;
  return (
    <div className="h-[260px] w-full" data-testid={testId}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
          <Tooltip />
          <Line type="monotone" dataKey="value" stroke="#1e3a8a" strokeWidth={2.5} dot={{ r: 4, fill: "#1e3a8a" }} activeDot={{ r: 6 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export function ActionsStatusChart({ data, testId = "chart-actions-status" }) {
  if (!data || data.every((d) => !d.value)) return <EmptyChart testId={testId} />;
  return (
    <div className="h-[260px] w-full" data-testid={testId}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
          <Tooltip />
          <Bar dataKey="value" radius={[6, 6, 0, 0]}>
            {data.map((entry, idx) => (
              <Cell key={idx} fill={COLORS_STATUS[entry.name] || "#94a3b8"} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
